import {
  DeserializedRecord,
  deserializeItem,
  deserializeManyRelationship,
  Deserializer,
  deserializeSingleRelationship,
  JSONAPIManyRelationship,
  JSONAPIRecord,
  JSONAPIReference,
  JSONAPISingleRelationship
} from "../Helpers";
import {Finding} from "../Finding";
import {Measurement} from "./Measurement";
import {deserializePatient, Patient} from "../Patient";
import {deserializePicture, Picture} from "./measurement/Picture";

interface GroupEvent extends DeserializedRecord<"event"> {
  description: string;
  level: "info" | "low" | "medium" | "high";
  vendorCode: string;
}

interface Group extends DeserializedRecord<"measurement_group"> {
  assessment?: string;
  ecgs: JSONAPIReference<"ecg">[];
  events: GroupEvent[];
  findings: JSONAPIReference<"finding">[] | Finding[];
  /**
   * The group is a transmission with measurements and documents
   */
  isTransmission: boolean;
  measuredAt: Date;
  measurements: JSONAPIReference<"measurement">[] | Measurement[];
  pictures: JSONAPIReference<"picture">[] | Picture[];
  patient?: JSONAPIReference<"patient"> | Patient;
  seenAt?: Date;
  seenBy?: JSONAPIReference<"physician">;
  source: string;
}

interface GroupData extends JSONAPIRecord {
  relationships: {
    ecgs: JSONAPIManyRelationship;
    events: JSONAPIManyRelationship;
    findings: JSONAPIManyRelationship;
    measurements: JSONAPIManyRelationship;
    patient: JSONAPISingleRelationship;
    pictures: JSONAPIManyRelationship;
    seen_by: JSONAPISingleRelationship;
  }
}

const deserializeGroup: Deserializer<Group, GroupData> = (data, included) => {
  const ecgs = deserializeManyRelationship<JSONAPIReference<"ecg">>(data.relationships.ecgs);
  const events = deserializeManyRelationship(data.relationships.events, included, deserializeItem<GroupEvent>);
  const findings = deserializeManyRelationship<JSONAPIReference<"finding">>(data.relationships.findings);
  const measurements = deserializeManyRelationship<JSONAPIReference<"measurement">>(data.relationships.measurements);
  const patient = deserializeSingleRelationship(data.relationships.patient, included, deserializePatient);
  const pictures = deserializeManyRelationship<JSONAPIReference<"picture">>(data.relationships.pictures);
  const seenBy = deserializeSingleRelationship<JSONAPIReference<"physician">>(data.relationships.seen_by);

  return {
    ...deserializeItem(data),
    ecgs,
    events,
    findings,
    measurements,
    patient,
    pictures,
    seenBy
  };
};

const deserializeGroupWithMembers: Deserializer<Group, GroupData> = (data, included) => {
  const ecgs = deserializeManyRelationship<JSONAPIReference<"ecg">>(data.relationships.ecgs, included);
  const events = deserializeManyRelationship(data.relationships.events, included, deserializeItem<GroupEvent>);
  const findings = deserializeManyRelationship<JSONAPIReference<"finding">>(data.relationships.findings, included);
  const measurements = deserializeManyRelationship<JSONAPIReference<"measurement">>(data.relationships.measurements, included);
  const patient = deserializeSingleRelationship(data.relationships.patient, included, deserializePatient);
  const pictures = deserializeManyRelationship<Picture>(data.relationships.pictures, included, deserializePicture);
  const seenBy = deserializeSingleRelationship<JSONAPIReference<"physician">>(data.relationships.seen_by);

  return {
    ...deserializeItem(data),
    ecgs,
    events,
    findings,
    measurements,
    patient,
    pictures,
    seenBy
  };
};

export {
  deserializeGroup,
  deserializeGroupWithMembers,
  Group,
  GroupEvent
};
